import React, { useState } from 'react';
import { Send, CheckCircle2, User, Building2, Mail, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { inquiriesAPI } from '../../services/api';

const SUBJECTS = [
  { value: 'reklam', label: 'Reklam' },
  { value: 'sponsorluk', label: 'Sponsorluk' },
  { value: 'isbirligi', label: 'İş Birliği' },
  { value: 'diger', label: 'Diğer' },
];

const InquiryForm = () => {
  const [form, setForm] = useState({
    full_name: '', company: '', email: '', phone: '', subject: 'reklam', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.full_name.trim().length < 2) return setError('Ad Soyad zorunludur');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return setError('Geçerli bir e-posta girin');
    if (form.message.trim().length < 10) return setError('Mesajınız en az 10 karakter olmalı');

    setSubmitting(true);
    try {
      await inquiriesAPI.create(form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Gönderilemedi, lütfen tekrar deneyin');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="bg-green-50 border border-green-200 rounded-lg p-8 text-center"
        data-testid="inquiry-success"
      >
        <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-gray-900 mb-1">Talebiniz alındı</h3>
        <p className="text-gray-600 text-sm">
          Mesajınızı aldık. En kısa sürede ekibimiz sizinle iletişime geçecektir.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-gray-200 rounded-lg p-6 md:p-8 space-y-5"
      data-testid="inquiry-form"
      noValidate
    >
      <div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">İletişim Formu</h2>
        <p className="text-sm text-gray-500">Bilgilerinizi bırakın, size en kısa sürede dönelim.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full name */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            <User className="w-4 h-4 inline mr-1" /> Ad Soyad <span className="text-red-600">*</span>
          </label>
          <Input
            value={form.full_name}
            onChange={update('full_name')}
            placeholder="Ahmet Yılmaz"
            required
            data-testid="inquiry-name"
          />
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            <Building2 className="w-4 h-4 inline mr-1" /> Şirket
          </label>
          <Input
            value={form.company}
            onChange={update('company')}
            placeholder="Şirket adı (opsiyonel)"
            data-testid="inquiry-company"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            <Mail className="w-4 h-4 inline mr-1" /> E-posta <span className="text-red-600">*</span>
          </label>
          <Input
            type="email"
            value={form.email}
            onChange={update('email')}
            placeholder="ornek@sirket.com"
            required
            data-testid="inquiry-email"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            <Phone className="w-4 h-4 inline mr-1" /> Telefon
          </label>
          <Input
            value={form.phone}
            onChange={update('phone')}
            placeholder="+90 5xx xxx xx xx"
            data-testid="inquiry-phone"
          />
        </div>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Konu <span className="text-red-600">*</span></label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="inquiry-subject-group">
          {SUBJECTS.map((s) => {
            const selected = form.subject === s.value;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setForm({ ...form, subject: s.value })}
                data-testid={`inquiry-subject-${s.value}`}
                className={`text-sm font-medium px-4 py-2.5 rounded-lg border transition-all ${
                  selected
                    ? 'bg-red-600 border-red-600 text-white shadow-sm'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-red-300 hover:text-red-600'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          <MessageSquare className="w-4 h-4 inline mr-1" /> Mesajınız <span className="text-red-600">*</span>
        </label>
        <Textarea
          value={form.message}
          onChange={update('message')}
          rows={5}
          placeholder="Talebinizi kısaca anlatın…"
          required
          data-testid="inquiry-message"
        />
      </div>

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2"
          data-testid="inquiry-error"
        >
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={submitting}
        className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white"
        data-testid="inquiry-submit"
      >
        <Send className="w-4 h-4 mr-2" />
        {submitting ? 'Gönderiliyor…' : 'Talebi Gönder'}
      </Button>

      <p className="text-xs text-gray-400 leading-relaxed">
        Form gönderildiğinde, bilgileriniz yalnızca size dönüş yapmak amacıyla kullanılacaktır.
      </p>
    </form>
  );
};

export default InquiryForm;
